import { onDocumentLoaded } from '@theme/utilities';

/**
 * Early access password form.
 *
 * The server decides whether a visitor is unlocked by comparing cart attributes with the collection's password
 * and revision. This element only sends the attempt and reloads, so a wrong password shows up as the form
 * rendering again. A sessionStorage flag set before the reload tells a failed attempt apart from a first visit
 * or from a visitor locked out by a password change. An unlocked page clears the flag (layout/theme.liquid),
 * because this element is not rendered there to clear it.
 */

class EarlyAccessGate extends HTMLElement {
  connectedCallback() {
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('form'));
    this.input = /** @type {HTMLInputElement} */ (this.querySelector('input[type="password"]'));
    this.button = /** @type {HTMLButtonElement} */ (this.querySelector('button[type="submit"]'));
    this.error = /** @type {HTMLElement} */ (this.querySelector('[role="alert"]'));
    this.buttonLabel = this.button.textContent;

    this.form.addEventListener('submit', (event) => this.#submit(event));

    if (this.#takePendingFlag()) {
      this.#showError(this.dataset.messageWrong);
    }
  }

  get #pendingKey() {
    return `early-access-pending-${this.dataset.collectionId}`;
  }

  /** @param {SubmitEvent} event */
  async #submit(event) {
    event.preventDefault();

    if (this.input.value.trim() === '') {
      this.#showError(this.dataset.messageEmpty);
      return;
    }

    this.#setSending(true);
    try {
      const response = await fetch(`${this.form.action}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
    } catch {
      this.#setSending(false);
      this.#showError(this.dataset.messageNetwork);
      return;
    }

    this.#setPendingFlag();
    window.location.reload();
  }

  /** @param {boolean} sending */
  #setSending(sending) {
    this.button.disabled = sending;
    this.button.textContent = sending ? this.dataset.messageSending ?? this.buttonLabel : this.buttonLabel;
  }

  /** @param {string | undefined} message */
  #showError(message) {
    this.error.textContent = message ?? '';
    this.input.setAttribute('aria-invalid', 'true');
    this.#focusInput();
  }

  /** Other page scripts can move focus while the page loads, so focus after load and on the next frame. */
  #focusInput() {
    onDocumentLoaded(() => requestAnimationFrame(() => this.input.focus()));
  }

  #setPendingFlag() {
    try {
      sessionStorage.setItem(this.#pendingKey, '1');
    } catch {
      // Storage can be unavailable in private modes; the visitor then sees no error text after a wrong attempt.
    }
  }

  #takePendingFlag() {
    try {
      const pending = sessionStorage.getItem(this.#pendingKey) === '1';
      sessionStorage.removeItem(this.#pendingKey);
      return pending;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('early-access-gate')) {
  customElements.define('early-access-gate', EarlyAccessGate);
}
